/**
 * Prioritization Tools Library
 * Predefined tools that can be applied to set up attributes and criteria
 */

import type { PrioritizationTool } from '../../types/prioritization/tools';
import type { Attribute } from '../../types/prioritization/lifecycle';

export const PRIORITIZATION_TOOLS: PrioritizationTool[] = [
  {
    id: 'rice',
    name: 'RICE Framework',
    description: 'Reach, Impact, Confidence, Effort - A popular prioritization framework for product teams',
    category: 'strategic',
    complexity: 'moderate',
    requiredAttributes: [
      {
        label: 'Reach',
        type: 'number',
        required: true,
        validation: { min: 1, max: 5 }
      },
      {
        label: 'Impact',
        type: 'select',
        required: true,
        options: ['Low', 'Medium', 'High']
      },
      {
        label: 'Confidence',
        type: 'number',
        required: true,
        validation: { min: 1, max: 5 }
      },
      {
        label: 'Effort',
        type: 'number',
        required: true,
        validation: { min: 1, max: 5 }
      }
    ],
    defaultCriteria: {
      attributeKeys: ['reach', 'impact', 'confidence', 'effort'],
      weights: {
        reach: 0.25,
        impact: 0.35,
        confidence: 0.20,
        effort: 0.20
      },
      framing: 'Compare items based on Reach × Impact × Confidence, divided by Effort. Focus on maximum value delivery. Use consistent scales across all items and rate confidence honestly.'
    },
    bestPractices: [
      'Use consistent scales across all items',
      'Rate confidence honestly - don\'t inflate estimates',
      'Revisit Effort estimates after initial scoring',
      'Calculate RICE score = (Reach × Impact × Confidence) / Effort'
    ],
    whenToUse: [
      'Product decisions',
      'Feature prioritization',
      'Team planning',
      'Multiple stakeholders involved'
    ],
    whenNotToUse: [
      'Quick decisions',
      'Solo work',
      'Very small item sets',
      'Highly subjective decisions'
    ],
    examples: [
      'Feature prioritization for product roadmap',
      'Team backlog management',
      'Quarterly planning cycles'
    ],
    relatedTools: ['ice', 'wsjf']
  },
  {
    id: 'ice',
    name: 'ICE Framework',
    description: 'Impact, Confidence, Effort - A simplified prioritization framework',
    category: 'strategic',
    complexity: 'simple',
    requiredAttributes: [
      {
        label: 'Impact',
        type: 'select',
        required: true,
        options: ['Low', 'Medium', 'High']
      },
      {
        label: 'Confidence',
        type: 'number',
        required: true,
        validation: { min: 1, max: 5 }
      },
      {
        label: 'Effort',
        type: 'number',
        required: true,
        validation: { min: 1, max: 5 }
      }
    ],
    defaultCriteria: {
      attributeKeys: ['impact', 'confidence', 'effort'],
      weights: {
        impact: 0.45,
        confidence: 0.35,
        effort: 0.20
      },
      framing: 'Compare items based on Impact, Confidence, and Effort. Prioritize items with high impact and confidence relative to effort required. Calculate ICE score = (Impact × Confidence) / Effort.'
    },
    bestPractices: [
      'Keep scoring simple and consistent',
      'Focus on Impact as the primary driver',
      'Use Confidence to account for uncertainty',
      'Revisit scores when new information emerges'
    ],
    whenToUse: [
      'Simpler decisions than RICE',
      'Quick prioritization needs',
      'Small to medium teams',
      'Feature backlog management'
    ],
    whenNotToUse: [
      'Complex multi-stakeholder decisions',
      'Portfolio-level prioritization',
      'When reach/scale matters significantly'
    ],
    examples: [
      'Sprint planning',
      'Quick feature triage',
      'Small team prioritization'
    ],
    relatedTools: ['rice', 'impact-effort']
  },
  {
    id: 'wsjf',
    name: 'WSJF (Weighted Shortest Job First)',
    description: 'Cost of Delay divided by Duration - Optimizes for business value delivery',
    category: 'strategic',
    complexity: 'complex',
    requiredAttributes: [
      {
        label: 'User Value',
        type: 'number',
        required: true,
        validation: { min: 1, max: 10 }
      },
      {
        label: 'Time Criticality',
        type: 'number',
        required: true,
        validation: { min: 1, max: 10 }
      },
      {
        label: 'Risk Reduction',
        type: 'number',
        required: true,
        validation: { min: 1, max: 10 }
      },
      {
        label: 'Duration',
        type: 'number',
        required: true,
        validation: { min: 1, max: 10 }
      }
    ],
    defaultCriteria: {
      attributeKeys: ['userValue', 'timeCriticality', 'riskReduction', 'duration'],
      weights: {
        userValue: 0.25,
        timeCriticality: 0.25,
        riskReduction: 0.20,
        duration: 0.30
      },
      framing: 'Calculate Cost of Delay = User Value + Time Criticality + Risk Reduction. Calculate WSJF = Cost of Delay / Duration. Prioritize highest WSJF scores first to optimize business value delivery.'
    },
    bestPractices: [
      'Focus on Cost of Delay components',
      'Accurately estimate Duration',
      'Revisit as context changes',
      'Use for portfolio-level decisions'
    ],
    whenToUse: [
      'Portfolio prioritization',
      'Large team coordination',
      'Strategic planning',
      'SAFe/Agile contexts'
    ],
    whenNotToUse: [
      'Quick decisions',
      'Small teams',
      'Simple tasks',
      'When duration is hard to estimate'
    ],
    examples: [
      'Portfolio prioritization',
      'Strategic planning',
      'Large team coordination'
    ],
    relatedTools: ['rice']
  },
  {
    id: 'impact-effort',
    name: 'Impact vs Effort Matrix',
    description: 'A simple 2×2 matrix plotting impact against effort to quickly categorize items',
    category: 'tactical',
    complexity: 'simple',
    requiredAttributes: [
      {
        label: 'Impact',
        type: 'select',
        required: true,
        options: ['Low', 'Medium', 'High']
      },
      {
        label: 'Effort',
        type: 'select',
        required: true,
        options: ['Low', 'Medium', 'High']
      }
    ],
    defaultCriteria: {
      attributeKeys: ['impact', 'effort'],
      weights: {
        impact: 0.60,
        effort: 0.40
      },
      framing: 'Plot items on a 2×2 matrix: High Impact/Low Effort (Quick Wins), High Impact/High Effort (Major Projects), Low Impact/Low Effort (Fill-ins), Low Impact/High Effort (Time Sinks). Focus on Quick Wins first.'
    },
    bestPractices: [
      'Fast and intuitive scoring',
      'Visual representation helps',
      'Good for quick decisions',
      'Focus on High Impact/Low Effort items first'
    ],
    whenToUse: [
      'Quick triage',
      'Daily task prioritization',
      'Solo decision making',
      'Small item sets'
    ],
    whenNotToUse: [
      'Complex multi-criteria decisions',
      'Large teams',
      'High-stakes decisions',
      'When confidence/urgency matters'
    ],
    examples: [
      'Daily task triage',
      'Feature prioritization',
      'Bug prioritization'
    ],
    relatedTools: ['rice', 'ice']
  },
  {
    id: 'moscow',
    name: 'MoSCoW Method',
    description: 'Categorizes items into Must have, Should have, Could have, and Won\'t have',
    category: 'operational',
    complexity: 'simple',
    requiredAttributes: [
      {
        label: 'MoSCoW Category',
        type: 'select',
        required: true,
        options: ['Must Have', 'Should Have', 'Could Have', 'Won\'t Have']
      },
      {
        label: 'Business Value',
        type: 'select',
        required: true,
        options: ['Low', 'Medium', 'High']
      }
    ],
    defaultCriteria: {
      attributeKeys: ['moscowCategory', 'businessValue'],
      weights: {
        moscowCategory: 0.70,
        businessValue: 0.30
      },
      framing: 'Categorize items as Must have (critical), Should have (important), Could have (nice-to-have), or Won\'t have (deferred). Focus on Must have items first, then Should have. Use Business Value to break ties within categories.'
    },
    bestPractices: [
      'Clear categorization',
      'Stakeholder alignment required',
      'Easy to communicate',
      'Review categorizations with team'
    ],
    whenToUse: [
      'Sprint planning',
      'Feature prioritization',
      'Team backlog management',
      'Stakeholder alignment needed'
    ],
    whenNotToUse: [
      'Solo decisions',
      'Complex scoring needed',
      'Large item sets',
      'When relative ranking matters more than categories'
    ],
    examples: [
      'Sprint planning',
      'Feature roadmap',
      'Product backlog'
    ],
    relatedTools: ['impact-effort']
  }
];

/**
 * Get tool by ID
 */
export function getToolById(id: string): PrioritizationTool | undefined {
  return PRIORITIZATION_TOOLS.find(tool => tool.id === id);
}

/**
 * Get tools by category
 */
export function getToolsByCategory(category: PrioritizationTool['category']): PrioritizationTool[] {
  return PRIORITIZATION_TOOLS.filter(tool => tool.category === category);
}

/**
 * Get tools by complexity
 */
export function getToolsByComplexity(complexity: PrioritizationTool['complexity']): PrioritizationTool[] {
  return PRIORITIZATION_TOOLS.filter(tool => tool.complexity === complexity);
}

/**
 * Generate attribute keys from tool's required attributes
 * Creates camelCase keys from labels (matches tool.defaultCriteria.attributeKeys)
 */
export function generateAttributeKeys(tool: PrioritizationTool): string[] {
  return tool.requiredAttributes.map(attr => {
    // Convert label to camelCase key (e.g., "User Value" -> "userValue")
    const words = attr.label.toLowerCase().split(/\s+/);
    const firstWord = words[0];
    const restWords = words.slice(1).map(w => w.charAt(0).toUpperCase() + w.slice(1));
    return firstWord + restWords.join('');
  });
}

/**
 * Apply tool to attribute management
 * Creates attributes from tool definition
 */
export function applyToolToAttributes(
  tool: PrioritizationTool,
  existingAttributes: Array<{ key: string }>
): { 
  attributesToCreate: Omit<Attribute, 'key'>[],
  existingKeys: string[]
} {
  const keys = generateAttributeKeys(tool);
  const existingKeys = existingAttributes.map(attr => attr.key.toLowerCase());
  
  const attributesToCreate = tool.requiredAttributes
    .map((attr, index) => {
      const generatedKey = keys[index];
      // Check if attribute already exists
      if (existingKeys.includes(generatedKey.toLowerCase())) {
        return null;
      }
      return attr;
    })
    .filter((attr): attr is Omit<Attribute, 'key'> => attr !== null);
  
  const matchedExistingKeys = keys.filter(key => 
    existingKeys.includes(key.toLowerCase())
  );
  
  return {
    attributesToCreate,
    existingKeys: matchedExistingKeys
  };
}

